import { useState } from 'react'
import { useStore, selectActiveClip } from '../store'
import { extractAudio16k } from '../lib/media'
import { transcribe } from '../lib/transcribe'
import { exportFormat } from '../lib/export'
import { detectFaceFocus } from '../lib/reframe'
import { getFormat } from '../lib/formats'
import type { Lang, Reframe } from '../types'

export function Toolbar() {
  const clips = useStore((s) => s.clips)
  const clip = useStore(selectActiveClip)
  const playing = useStore((s) => s.playing)
  const busy = useStore((s) => s.busy)
  const lang = useStore((s) => s.lang)
  const logo = useStore((s) => s.logo)
  const exportFormats = useStore((s) => s.exportFormats)
  const reframe = useStore((s) => s.reframe)
  const style = useStore((s) => s.style)
  const studioAudio = useStore((s) => s.studioAudio)
  const setLang = useStore((s) => s.setLang)
  const setPlaying = useStore((s) => s.setPlaying)
  const setClipWords = useStore((s) => s.setClipWords)
  const setStatus = useStore((s) => s.setStatus)
  const setBusy = useStore((s) => s.setBusy)
  const removeAllFillers = useStore((s) => s.removeAllFillers)
  const restoreAll = useStore((s) => s.restoreAll)
  const setShowAbout = useStore((s) => s.setShowAbout)
  const reset = useStore((s) => s.reset)

  const [exportPct, setExportPct] = useState<number | null>(null)

  const onTranscribe = async () => {
    if (!clip || busy) return
    setBusy(true)
    try {
      setStatus(`Đang trích xuất âm thanh (${clip.name})…`)
      const audio = await extractAudio16k(clip.file, setStatus)
      const { words } = await transcribe(audio, lang, setStatus)
      if (words.length === 0) {
        setStatus('⚠️ Không nhận được lời nói nào. Clip có thoại không?')
      } else {
        setClipWords(clip.id, words)
        setStatus(`✅ Đã phiên âm ${words.length} từ. Click vào chữ để cắt!`)
      }
    } catch (e) {
      setStatus(`⚠️ Lỗi phiên âm: ${(e as Error).message}`)
    } finally {
      setBusy(false)
    }
  }

  const onExport = async () => {
    if (clips.length === 0 || busy) return
    setPlaying(false)
    setBusy(true)
    setExportPct(0)
    try {
      const needsCrop = exportFormats.some((k) => k !== 'original')

      // Giải quyết điểm lấy nét cho reframe (chỉ khi có format bị crop).
      let rf: Reframe = reframe
      if (needsCrop && reframe.mode === 'face') {
        setStatus('Đang dò khuôn mặt để reframe…')
        const focus = await detectFaceFocus(clips[0])
        if (focus) {
          rf = { mode: 'manual', fx: focus.fx, fy: focus.fy }
        } else {
          rf = { mode: 'center', fx: 0.5, fy: 0.5 }
          setStatus('Không dò được khuôn mặt (hoặc trình duyệt không hỗ trợ) — dùng crop giữa.')
        }
      }

      const total = exportFormats.length
      for (let i = 0; i < total; i++) {
        const key = exportFormats[i]
        const fmt = getFormat(key)
        setStatus(`Đang xuất ${i + 1}/${total}: ${fmt.short}…`)
        const { blob, ext } = await exportFormat(
          clips,
          { logo, formatKey: key, reframe: rf, style, studioAudio },
          (r) => setExportPct(Math.round(r * 100)),
        )
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `brandcut-${fmt.short.replace(/[:/ ]/g, '')}.${ext}`
        a.click()
        URL.revokeObjectURL(url)
      }
      setStatus(`✅ Đã xuất ${total} phiên bản (ghép ${clips.length} clip${logo.url ? ' + logo' : ''}).`)
    } catch (e) {
      setStatus(`⚠️ Lỗi xuất: ${(e as Error).message}`)
    } finally {
      setBusy(false)
      setExportPct(null)
    }
  }

  const hasClips = clips.length > 0

  return (
    <div className="toolbar">
      <div className="brand">🎬 BrandCut</div>

      <button className="btn primary" disabled={!hasClips || busy} onClick={() => setPlaying(!playing)}>
        {playing ? '⏸ Tạm dừng' : '▶ Phát'}
      </button>

      <select
        className="select"
        value={lang}
        disabled={busy}
        onChange={(e) => setLang(e.target.value as Lang)}
        title="Ngôn ngữ phiên âm"
      >
        <option value="auto">🌐 Tự động</option>
        <option value="vietnamese">🇻🇳 Tiếng Việt</option>
        <option value="english">🇬🇧 English</option>
      </select>

      <button className="btn" disabled={!clip || busy} onClick={onTranscribe}>
        🎙️ Phiên âm (AI)
      </button>

      <button
        className="btn"
        disabled={!clip?.transcribed || busy}
        onClick={removeAllFillers}
        title="Cắt tất cả từ đệm trong clip này"
      >
        ✂️ Xóa từ đệm
      </button>

      <button className="btn ghost" disabled={!clip?.transcribed || busy} onClick={restoreAll}>
        ↺ Khôi phục
      </button>

      <div className="spacer" />

      <button className="btn success" disabled={!hasClips || busy} onClick={onExport}>
        {exportPct === null
          ? `⬇ Xuất ${exportFormats.length} tỉ lệ`
          : `Đang xuất… ${exportPct}%`}
      </button>

      <button className="btn ghost" disabled={busy} onClick={() => setShowAbout(true)} title="Giới thiệu & bảng giá">
        ℹ️
      </button>

      <button className="btn ghost" disabled={busy} onClick={reset} title="Dự án mới (xóa hết)">
        🗑
      </button>
    </div>
  )
}
